import { SITE_CONFIG } from "../app/config";
import { LegalLayout } from "./LegalLayout";

export const PrivacyPage = (): JSX.Element => (
  <LegalLayout title="Privacy Policy" path="/privacy">
    <section>
      <h2>1. Introduction</h2>
      <p>
        {SITE_CONFIG.companyLegalName} ("we", "our", or "us") operates {SITE_CONFIG.name}. This Privacy Policy 
        explains how we collect, use, and protect information when you use our Service.
      </p>
      <p>
        <strong>Summary:</strong> We collect minimal data. We only track anonymous page views using Umami 
        analytics. We do not store personal information, create user accounts, or use cookies for tracking.
      </p>
    </section>

    <section>
      <h2>2. Information We Collect</h2>
      
      <h3>2.1 Analytics Data (Umami)</h3>
      <p>
        We use Umami, a privacy-focused analytics tool, to understand how visitors use our Service. 
        Umami collects:
      </p>
      <ul>
        <li>Page views and navigation patterns</li>
        <li>Referral sources (how you found us)</li>
        <li>Device type and browser (anonymized)</li>
        <li>Country/region (approximate, not precise location)</li>
      </ul>
      <p>
        <strong>Important:</strong> Umami does not use cookies, does not track across websites, and does 
        not collect personally identifiable information. All data is anonymized and aggregated.
      </p>

      <h3>2.2 Spotify Playlist Data</h3>
      <p>
        When you submit a Spotify playlist URL, we temporarily process:
      </p>
      <ul>
        <li>The playlist ID from the URL you provide</li>
        <li>Track and album information from that playlist</li>
      </ul>
      <p>
        This data is processed in real-time to generate your results and is <strong>not stored</strong> on 
        our servers after your session ends.
      </p>

      <h3>2.3 What We Do NOT Collect</h3>
      <ul>
        <li>Personal information (name, email, address)</li>
        <li>Spotify account credentials or login information</li>
        <li>Payment information (all purchases happen on third-party sites)</li>
        <li>Tracking cookies or persistent identifiers</li>
      </ul>
    </section>

    <section>
      <h2>3. How We Use Information</h2>
      <p>The limited data we collect is used to:</p>
      <ul>
        <li>Provide and improve the Service</li>
        <li>Understand usage patterns to enhance user experience</li>
        <li>Monitor Service performance and reliability</li>
      </ul>
    </section>

    <section>
      <h2>4. Third-Party Services</h2>
      
      <h3>4.1 Affiliate Links ({SITE_CONFIG.affiliatePartner})</h3>
      <p>
        We participate in the {SITE_CONFIG.affiliatePartner} affiliate program. When you click on links 
        to purchase CDs or vinyl records, you will be redirected to third-party websites. These sites 
        have their own privacy policies and may collect information according to their terms.
      </p>
      <p>
        We earn a commission on qualifying purchases made through these links, at no additional cost to you.
      </p>

      <h3>4.2 Spotify API</h3>
      <p>
        We use Spotify's public API to retrieve playlist information. We do not access your Spotify account 
        directly. Only publicly available playlist data is retrieved based on the URL you provide.
      </p>
    </section>

    <section>
      <h2>5. Data Retention</h2>
      <p>
        We do not store personal data. Analytics data collected by Umami is retained in aggregate form 
        and cannot be traced back to individual users.
      </p>
    </section>

    <section>
      <h2>6. Your Rights</h2>
      <p>Since we do not collect personal data, there is no personal information to access, modify, or delete. 
      If you have any concerns, please contact us.</p>
    </section>

    <section>
      <h2>7. Children's Privacy</h2>
      <p>
        Our Service is not directed to children under 13. We do not knowingly collect information from 
        children under 13.
      </p>
    </section>

    <section>
      <h2>8. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any changes by updating 
        the "Last updated" date at the top of this page.
      </p>
    </section>

    <section>
      <h2>9. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy, please contact us at{" "}
        <a href={`mailto:${SITE_CONFIG.contactEmail}`}>{SITE_CONFIG.contactEmail}</a>.
      </p>
    </section>
  </LegalLayout>
);

